import { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Html5Qrcode } from "html5-qrcode";
import { Loader2, Search, UserCheck, Users, DoorOpen, ScanLine } from "lucide-react";
import { toast } from "sonner";

export default function CheckIn() {
  const { eventId } = useOutletContext();
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [scanOpen, setScanOpen] = useState(false);
  const scannerRef = useRef(null);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get(`/guests/${eventId}`);
    setGuests(data);
    setLoading(false);
  };
  useEffect(() => {
    if (eventId) load();
  }, [eventId]);

  useEffect(() => {
    if (!scanOpen) return;
    let active = true;
    const scanner = new Html5Qrcode("qr-reader");
    scannerRef.current = scanner;
    scanner
      .start({ facingMode: "environment" }, { fps: 10, qrbox: 240 }, async (decoded) => {
        if (!active) return;
        active = false;
        const m = decoded.match(/\/invite\/([^/?#]+)/);
        const tok = m ? m[1] : decoded;
        try {
          const { data } = await api.patch(`/checkin/by-token/${tok}`);
          toast.success(`${data.primary_guest_name} checked in`);
          load();
        } catch {
          toast.error("Unrecognised invitation code");
        }
        try { await scanner.stop(); } catch (e) {}
        setScanOpen(false);
      })
      .catch(() => toast.error("Could not access camera. Please allow camera permission."));
    return () => {
      active = false;
      const s = scannerRef.current;
      if (s && s.isScanning) {
        s.stop().then(() => s.clear()).catch(() => {});
      }
    };
  }, [scanOpen]);

  const toggle = async (g) => {
    const next = !g.checked_in;
    setGuests((prev) => prev.map((x) => (x.id === g.id ? { ...x, checked_in: next } : x)));
    try {
      await api.patch(`/guests/${g.id}/checkin`, { checked_in: next });
      if (next) toast.success(`${g.primary_guest_name} checked in`);
    } catch {
      toast.error("Could not update");
      load();
    }
  };

  const stats = useMemo(() => {
    const confirmed = guests.filter((g) => g.rsvp?.status === "attending");
    const expectedHeads = confirmed.reduce((s, g) => s + (g.rsvp?.adults_attending || 0) + (g.rsvp?.children_attending || 0), 0);
    const arrivedParties = guests.filter((g) => g.checked_in);
    const arrivedHeads = arrivedParties.reduce((s, g) => s + (g.rsvp?.adults_attending || 0) + (g.rsvp?.children_attending || 0), 0);
    return { parties: confirmed.length, expectedHeads, arrivedParties: arrivedParties.length, arrivedHeads };
  }, [guests]);

  const filtered = guests
    .filter((g) => g.rsvp?.status === "attending")
    .filter((g) => `${g.primary_guest_name} ${g.family_name}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => Number(a.checked_in) - Number(b.checked_in));

  return (
    <div data-testid="checkin-page">
      <h1 className="mb-1 text-2xl font-bold tracking-tight">Digital Check-In</h1>
      <p className="mb-6 text-sm text-[#525252]">Mark guests as they arrive on the day</p>

      <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
        {[
          { label: "Confirmed Parties", value: stats.parties, Icon: Users },
          { label: "Expected Heads", value: stats.expectedHeads, Icon: Users },
          { label: "Parties Arrived", value: stats.arrivedParties, Icon: DoorOpen },
          { label: "Heads Arrived", value: stats.arrivedHeads, Icon: UserCheck },
        ].map((c) => (
          <div key={c.label} className="rounded-md border border-[#E5E5E5] bg-white p-5">
            <c.Icon className="mb-3 h-5 w-5 text-[#1A362D]" />
            <div className="text-3xl font-bold" data-testid={`checkin-stat-${c.label.toLowerCase().replace(/ /g, "-")}`}>{c.value}</div>
            <div className="mt-1 text-xs uppercase tracking-wide text-[#525252]">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="mb-4 flex max-w-xl items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#525252]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search confirmed guests..." className="pl-9" data-testid="checkin-search" />
        </div>
        <Button variant="outline" onClick={() => setScanOpen(true)} data-testid="scan-checkin-button">
          <ScanLine className="mr-2 h-4 w-4" /> Scan QR
        </Button>
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#1A362D]" /></div>
      ) : (
        <div className="space-y-2">
          {filtered.map((g) => (
            <div key={g.id} className={`flex items-center justify-between rounded-md border p-4 transition-colors ${g.checked_in ? "border-[#1A362D]/30 bg-[#F4EFE6]" : "border-[#E5E5E5] bg-white"}`} data-testid={`checkin-row-${g.id}`}>
              <div>
                <div className="font-medium">{g.primary_guest_name}</div>
                <div className="text-xs text-[#525252]">{g.family_name} · {(g.rsvp?.adults_attending || 0)} adults · {(g.rsvp?.children_attending || 0)} children</div>
              </div>
              <div className="flex items-center gap-3">
                {g.checked_in && <Badge className="bg-[#1A362D] text-white">Arrived</Badge>}
                <Button
                  onClick={() => toggle(g)}
                  variant={g.checked_in ? "outline" : "default"}
                  className={g.checked_in ? "" : "bg-[#1A362D] hover:bg-[#12271f]"}
                  data-testid={`checkin-toggle-${g.id}`}
                >
                  {g.checked_in ? "Undo" : "Check In"}
                </Button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="rounded-md border border-[#E5E5E5] bg-white p-10 text-center text-[#525252]">No confirmed guests yet.</div>}
        </div>
      )}

      <Dialog open={scanOpen} onOpenChange={setScanOpen}>
        <DialogContent className="max-w-md" data-testid="scan-dialog">
          <DialogHeader>
            <DialogTitle>Scan Guest QR</DialogTitle>
          </DialogHeader>
          <div id="qr-reader" className="w-full overflow-hidden rounded-lg" />
          <p className="text-center text-xs text-[#525252]">Point the camera at a guest's invitation QR code to check them in.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
